import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getTaskMasterDropdown } from '../../task-master/api/taskMasterApi'
import {
  archiveJobMaster,
  deleteJobMaster,
  listJobMasters,
} from '../api/jobMasterApi'

const DEFAULT_PAGE_SIZE = 10

function normalizeStatus(item) {
  if (Number(item?.is_archive) === 1) return 'Inactive'
  if (Number(item?.is_approved) === 0 || item?.approval_status === 'PENDING') {
    return 'Pending Review'
  }
  return 'Active'
}

export function useJobMasterListScreen() {
  const [searchText, setSearchText] = useState('')
  const [taskMasterId, setTaskMasterId] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [items, setItems] = useState([])
  const [taskMasterOptions, setTaskMasterOptions] = useState([])
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
    }

    if (statusFilter === 'inactive') {
      params.archive = true
    } else if (statusFilter === 'pending') {
      params.pending_review = true
      params.archive = false
    } else {
      params.archive = false
    }

    if (searchText.trim()) {
      params.search = searchText.trim()
    }

    if (taskMasterId) {
      params.taskMasterId = Number(taskMasterId)
    }

    return params
  }, [page, pageSize, searchText, statusFilter, taskMasterId])

  useEffect(() => {
    let active = true

    async function loadTaskMasterOptions() {
      try {
        const options = await getTaskMasterDropdown()
        if (!active) return
        setTaskMasterOptions(options)
      } catch {
        if (!active) return
        setTaskMasterOptions([])
      }
    }

    loadTaskMasterOptions()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    async function loadJobMasters() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listJobMasters(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          totalCount: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load job masters.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadJobMasters()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, reloadTick])

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        code: item.job_type_code || '-',
        name: item.job_type_name || '-',
        parentTaskMasterId: item.parent_task_master_id || null,
        parentTaskMasterName: item.parent_task_type_name || '-',
        defaultEstimatedHours: item.default_estimated_hours || 0.0,
        sequenceOrder: item.sequence_order || 0,
        slaHours: item.sla_hours || null,
        status: normalizeStatus(item),
        isArchived: Number(item?.is_archive) === 1,
      })),
    [items],
  )

  const totalPages = useMemo(() => {
    if (!meta.totalCount || !meta.pageSize) return meta.hasMore ? page + 1 : page
    return Math.max(1, Math.ceil(meta.totalCount / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.totalCount, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  function clearFeedback() {
    setError('')
    setSuccess('')
  }

  function setSearchTextAndReset(value) {
    clearFeedback()
    setSearchText(value)
    setPage(1)
  }

  function setTaskMasterIdAndReset(value) {
    clearFeedback()
    setTaskMasterId(value)
    setPage(1)
  }

  function setStatusFilterAndReset(value) {
    clearFeedback()
    setStatusFilter(value)
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  async function handleRowDeleteAction(row) {
    clearFeedback()
    setIsMutating(true)

    try {
      if (row.isArchived) {
        await deleteJobMaster(row.id)
        setSuccess('Job Master deleted permanently.')
      } else {
        await archiveJobMaster(row.id)
        setSuccess('Job Master archived successfully.')
      }

      setReloadTick((prev) => prev + 1)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to update job master status.'))
    } finally {
      setIsMutating(false)
    }
  }

  return {
    state: {
      searchText,
      taskMasterId,
      statusFilter,
      page,
      totalPages,
      totalCount: meta.totalCount,
      hasMore: meta.hasMore,
      isLoading,
      isMutating,
      error,
      success,
      rows,
      taskMasterOptions,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setSearchText: setSearchTextAndReset,
      setTaskMasterId: setTaskMasterIdAndReset,
      setStatusFilter: setStatusFilterAndReset,
      goToPage,
      goToPreviousPage,
      goToNextPage,
      handleRowDeleteAction,
    },
  }
}
